import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PANIPAT_BOUNDS, PANIPAT_CENTER, SEARCH_CONFIG } from '../utils/locationConstants';
import { reverseGeocodeLocation, searchLocation } from '../redux_files/actions/orderFormActions';
import { THEME } from '../utils/theme';

const LocationPicker = ({ onLocationSelect, initialLocation = null }) => {
  const reduxDispatch = useDispatch();
  const { locationSuggestions = [], isSearchingLocation = false, deliveryLocation = '' } = useSelector(state => state.orderForm || {});

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [locationAddress, setLocationAddress] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [locationError, setLocationError] = useState('');
  const markerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  
  // Check if location is within Panipat boundaries
  const isWithinPanipat = useCallback((location) => {
    return (
      location.lat >= PANIPAT_BOUNDS.minLat &&
      location.lat <= PANIPAT_BOUNDS.maxLat &&
      location.lng >= PANIPAT_BOUNDS.minLng &&
      location.lng <= PANIPAT_BOUNDS.maxLng
    );
  }, []);

  const reverseGeocode = useCallback((location) => {
    // Dispatch saga action to reverse geocode - saga will update Redux state
    reduxDispatch(reverseGeocodeLocation(location.lat, location.lng));
  }, [reduxDispatch]);

  // Watch for changes in Redux deliveryLocation and update local state
  useEffect(() => {
    if (deliveryLocation) {
      setLocationAddress(deliveryLocation);
    }
  }, [deliveryLocation]);

  // Forward geocoding: Convert address to coordinates using backend API
  const searchLocation_backend = (query) => {
    if (!query.trim()) {
      setSearchSuggestions([]);
      setIsSearching(false);
      return;
    }

    try {
      // Dispatch Redux Saga action for location search
      reduxDispatch(searchLocation(query));
      setLocationError('');
    } catch (error) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      setLocationError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input change with debouncing
  const handleSearchChange = (value) => {
    setSearchInput(value);
    setLocationError('');

    // Clear any pending debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (value.trim().length > 2) {
      setIsSearching(true);
      searchDebounceRef.current = setTimeout(() => {
        searchLocation_backend(value);
      }, SEARCH_CONFIG.debounceTime);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
    }
  };

  // Update suggestions from Redux store
  useEffect(() => {
    if (locationSuggestions && locationSuggestions.length > 0) {
      const mappedSuggestions = locationSuggestions.map(item => ({
        lat: item.lat,
        lng: item.lng,
        address: item.address || item.formatted,
        name: item.name || item.address_line1 || 'Location',
        isInPanipat: isWithinPanipat({lat: item.lat, lng: item.lng})
      }));
      
      setSearchSuggestions(mappedSuggestions);
      setShowSuggestions(true);
      setIsSearching(false);
    } else if (!isSearchingLocation && searchInput.trim().length > 0) {
      // Only show "no results" if search is complete and input exists
      if (locationSuggestions?.length === 0) {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }
  }, [locationSuggestions, isSearchingLocation]);

  // Handle suggestion click
  const handleSuggestionClick = useCallback((suggestion) => {
    const newLocation = {
      lat: suggestion.lat,
      lng: suggestion.lng
    };
    
    setSelectedLocation(newLocation);
    setLocationAddress(suggestion.address);
    setSearchInput(suggestion.address);
    setShowSuggestions(false);
    setSearchSuggestions([]);
    
    // Show warning if location is outside Panipat, but allow selection
    if (!isWithinPanipat(newLocation)) {
      setLocationError('⚠️ The selected location is outside Panipat. Delivery may not be possible. Please continue only if you agree.');
    } else {
      setLocationError('');
    }
    
  }, [isWithinPanipat]);

  // Load Leaflet library
  useEffect(() => {
    const initMapWhenReady = () => {
      // Load Leaflet CSS
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
        document.head.appendChild(link);
      }

      // Load Leaflet JS
      if (!window.L) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
        script.async = true;
        script.onload = () => {
          setTimeout(() => setMapLoaded(true), 100);
        };
        document.body.appendChild(script);
      } else {
        setMapLoaded(true);
      }
    };

    initMapWhenReady();
  }, []);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.L) return;
    if (mapInstanceRef.current) return; // Only initialize once

    // Center map on Panipat, Haryana
    const mapCenter = initialLocation || PANIPAT_CENTER;
    const map = window.L.map(mapRef.current).setView([mapCenter.lat, mapCenter.lng], 15);
    mapInstanceRef.current = map;

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    // Add marker
    const initialMarkerPos = selectedLocation || initialLocation || PANIPAT_CENTER;
    markerRef.current = window.L.marker([initialMarkerPos.lat, initialMarkerPos.lng]).addTo(map);

    // Handle map click
    const handleMapClick = (e) => {
      const newLocation = {
        lat: e.latlng.lat,
        lng: e.latlng.lng,
      };
      
      setSelectedLocation(newLocation);
      
      // Show warning if location is outside Panipat, but allow selection
      if (!isWithinPanipat(newLocation)) {
        setLocationError('⚠️ The selected location is outside Panipat. Delivery may not be possible. Please continue only if you agree.');
      } else {
        setLocationError('');
      }
      
      reverseGeocode(newLocation);
    };

    map.on('click', handleMapClick);

    return () => {
      map.off('click', handleMapClick);
    };
  }, [mapLoaded, initialLocation, reverseGeocode, isWithinPanipat]);

  // Update marker position when selected location changes
  useEffect(() => {
    if (!selectedLocation || !markerRef.current) return;
    
    markerRef.current.setLatLng([selectedLocation.lat, selectedLocation.lng]);
    
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([selectedLocation.lat, selectedLocation.lng], 15);
    }
  }, [selectedLocation]);

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        
        setSelectedLocation(newLocation);
        
        // Show warning if location is outside Panipat, but allow selection
        if (!isWithinPanipat(newLocation)) {
          setLocationError('⚠️ The selected location is outside Panipat. Delivery may not be possible. Please continue only if you agree.');
        } else {
          setLocationError('');
        }
        
        if (markerRef.current && mapInstanceRef.current) {
          mapInstanceRef.current.setView([newLocation.lat, newLocation.lng], 15);
          markerRef.current.setLatLng([newLocation.lat, newLocation.lng]);
        }
        
        reverseGeocode(newLocation);
      });
    }
  };

  if (!mapLoaded) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading Map...</div>;
  }

  // Styles object with THEME colors
  const styles = {
    loadingContainer: {
      padding: '20px',
      textAlign: 'center'
    },
    searchBox: {
      marginBottom: '15px',
      position: 'relative'
    },
    searchInput: {
      width: '100%',
      padding: '10px 12px',
      paddingRight: searchInput || isSearching ? '40px' : '12px',
      border: `2px solid ${THEME.border.light}`,
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box',
      transition: 'border-color 0.3s ease',
      color: THEME.text.primary
    },
    clearButton: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: '18px',
      color: THEME.text.disabled,
      padding: '0',
      lineHeight: '1',
      transition: 'color 0.2s ease'
    },
    searchingText: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: '12px',
      color: THEME.text.secondary
    },
    suggestionsDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: THEME.background.main,
      border: `1px solid ${THEME.border.light}`,
      borderRadius: '6px',
      marginTop: '4px',
      maxHeight: '200px',
      overflowY: 'auto',
      zIndex: 3000,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    suggestionItem: {
      padding: '10px 12px',
      cursor: 'pointer',
      fontSize: '13px',
      color: THEME.text.primary,
      transition: 'background-color 0.2s ease',
      borderBottom: `1px solid ${THEME.border.light}`
    },
    suggestionItemLast: {
      padding: '10px 12px',
      cursor: 'pointer',
      fontSize: '13px',
      color: THEME.text.primary,
      transition: 'background-color 0.2s ease',
      borderBottom: 'none'
    },
    suggestionName: {
      fontWeight: '500',
      color: THEME.primary.main
    },
    suggestionAddress: {
      fontSize: '12px',
      color: THEME.text.secondary,
      marginTop: '2px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    mapWrapper: {
      width: '100%',
      height: '300px',
      borderRadius: '8px'
    },
    buttonContainer: {
      marginTop: '12px',
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end'
    },
    confirmButton: {
      padding: '10px 16px',
      backgroundColor: THEME.success.main,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'background-color 0.2s ease'
    },
    controlsContainer: {
      marginTop: '15px',
      display: 'flex',
      gap: '10px'
    },
    currentLocationButton: {
      flex: 1,
      padding: '10px 15px',
      backgroundColor: THEME.primary.main,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease'
    },
    errorContainer: {
      marginTop: '15px',
      padding: '12px',
      backgroundColor: THEME.error.light,
      borderLeft: `4px solid ${THEME.error.main}`,
      borderRadius: '4px',
      fontSize: '13px',
      color: THEME.error.darkText,
      wordWrap: 'break-word'
    },
    successContainer: {
      marginTop: '15px',
      padding: '12px',
      backgroundColor: THEME.success.background,
      borderLeft: `4px solid ${THEME.success.main}`,
      borderRadius: '4px',
      fontSize: '13px',
      color: THEME.success.dark,
      wordWrap: 'break-word',
      wordBreak: 'break-word',
      whiteSpace: 'normal',
      lineHeight: '1.5'
    },
    successAddressText: {
      marginTop: '6px',
      fontSize: '12px',
      fontWeight: 'normal'
    },
    infoContainer: {
      marginTop: '15px',
      padding: '12px',
      backgroundColor: THEME.warning.light,
      borderLeft: `4px solid ${THEME.warning.main}`,
      borderRadius: '4px',
      fontSize: '13px',
      color: THEME.warning.dark
    }
  };

  return (
    <div className='location-picker-container'>
      {/* Search Box */}
      <div className='location-search-box' style={styles.searchBox}>
        <input
          type='text'
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder='🔍 Search for a location or address...'
          className='form-input'
          style={styles.searchInput}
        />
        
        {searchInput && !isSearching && (
          <button
            type='button'
            onClick={() => {
              setSearchInput('');
              setSearchSuggestions([]);
              setShowSuggestions(false);
              setLocationError('');
              if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
              }
            }}
            style={styles.clearButton}
            onMouseEnter={(e) => e.target.style.color = THEME.text.primary}
            onMouseLeave={(e) => e.target.style.color = THEME.text.disabled}
            aria-label='Clear search'
          >
            ✕
          </button>
        )}
        
        {isSearching && (
          <div style={styles.searchingText}>
            Searching...
          </div>
        )}

        {/* Search Suggestions Dropdown */}
        {showSuggestions && searchSuggestions && searchSuggestions.length > 0 && (
          <div className='location-suggestions' style={styles.suggestionsDropdown}>
            {searchSuggestions.map((suggestion, index) => (
              <div
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                style={index < searchSuggestions.length - 1 ? styles.suggestionItem : styles.suggestionItemLast}
                onMouseEnter={(e) => e.target.style.backgroundColor = THEME.background.hover}
                onMouseLeave={(e) => e.target.style.backgroundColor = THEME.background.main}
              >
                <div style={styles.suggestionName}>{suggestion.name}</div>
                <div style={styles.suggestionAddress}>
                  {suggestion.address}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div 
        ref={mapRef}
        className='location-map-wrapper'
        style={styles.mapWrapper}
      />
      
      <div style={styles.buttonContainer}>
        <button
          type='button'
          className='location-button'
          onClick={() => {
            if (!selectedLocation || !locationAddress) {
              setLocationError('Please select a location before confirming.');
              return;
            }
            setLocationError('');
            onLocationSelect(selectedLocation, locationAddress);
          }}
          style={styles.confirmButton}
        >
          Confirm Location
        </button>
      </div>
      
      <div className='location-controls' style={styles.controlsContainer}>
        <button
          className='location-button'
          onClick={handleCurrentLocation}
          style={styles.currentLocationButton}
        >
          📍 Use Current Location
        </button>
      </div>

      {locationError && (
        <div style={styles.errorContainer}>
          <strong>⚠️ Location Error:</strong> {locationError}
        </div>
      )}
      
      {locationAddress && (
        <div className='location-display' style={styles.successContainer}>
          <strong>✓ Selected Location:</strong>
          <div style={styles.successAddressText}>{locationAddress}</div>
        </div>
      )}
      
      {!locationAddress && selectedLocation && !locationError && (
        <div style={styles.infoContainer}>
          Click on the map, search for a location, or tap the current location button
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
